import clsx from 'clsx'
import { type ChangeEvent, type ReactNode, useEffect, useState } from 'react'

import { ValidatedForm } from '@fv/client-components'

import { AdminButton } from '@/components/shared/AdminButton'
import { AdminCode } from '@/components/shared/AdminCode'
import { AdminFileInput } from '@/components/shared/AdminFileInput'
import { AdminFormGroup } from '@/components/shared/AdminFormGroup'
import { AdminLink } from '@/components/shared/AdminLink'
import { AdminLoading } from '@/components/shared/AdminLoading'
import { fileToBase64 } from '@/utils'

type CustomTab<TResult> = {
  id: string
  label: string
  render: (result: TResult | undefined, fileData: string | null) => ReactNode
}

type DocumentTesterProps<TResult> = {
  title: string
  description?: string
  buttonText?: string
  onSubmit?: (base64File: string) => Promise<TResult>
  isLoading: boolean
  result?: TResult
  customTabs?: CustomTab<TResult>[]
  defaultTab?: string
  url?: string
}

export const DocumentTester = <TResult,>({
  title,
  description,
  buttonText = 'Submit',
  onSubmit,
  isLoading,
  result,
  customTabs = [],
  defaultTab = 'preview',
  url,
}: DocumentTesterProps<TResult>) => {
  const [fileData, setFileData] = useState<string>()
  const [previewUrl, setPreviewUrl] = useState<string>()
  const [activeTab, setActiveTab] = useState(defaultTab)

  // CSP disallows `data:` in frame-src, so the preview iframe uses a blob URL.
  useEffect(() => {
    if (!previewUrl) return
    return () => URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    setPreviewUrl(file ? URL.createObjectURL(file) : undefined)
    setFileData(file ? await fileToBase64(file) : undefined)
  }

  const handleSubmit = async () => {
    if (fileData) {
      await onSubmit(fileData.split('base64,')[1])
    }
  }

  const allTabs = [
    { id: 'preview', label: 'Preview' },
    { id: 'parsedData', label: 'Parsed Data' },
    ...customTabs.map(tab => ({ id: tab.id, label: tab.label })),
  ]

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h3 className="text-lg">{title}</h3>
        {description && <p className="text-sm">{description}</p>}
      </div>

      {onSubmit && (
        <ValidatedForm onValidSubmit={handleSubmit}>
          <AdminFormGroup label="Document" controlClass="flex join">
            <AdminFileInput onChange={handleFileChange} />
            <AdminButton
              type="submit"
              className="join-item btn-primary"
              loading={isLoading}
              solid
            >
              {buttonText}
            </AdminButton>
          </AdminFormGroup>
        </ValidatedForm>
      )}

      {isLoading && <AdminLoading />}

      <div>
        <div className="tabs tabs-border mb-2 w-full border-t border-base-content/30">
          {allTabs.map(tab => (
            <AdminLink
              key={tab.id}
              className={clsx('tab', activeTab === tab.id && 'tab-active')}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </AdminLink>
          ))}
        </div>

        {activeTab === 'preview' && <Preview file={url ?? previewUrl} />}
        {activeTab === 'parsedData' && <ParsedData result={result} />}
        {customTabs.map(
          tab =>
            activeTab === tab.id && (
              <div key={tab.id}>{tab.render(result, fileData)}</div>
            ),
        )}
      </div>
    </div>
  )
}

type PreviewProps = {
  file: string | null
}

const PREVIEW_SCHEMES = ['http:', 'https:', 'blob:']

const toPreviewSrc = (file: string) => {
  try {
    const url = new URL(file, window.location.origin)
    return PREVIEW_SCHEMES.includes(url.protocol) ? url.toString() : null
  } catch {
    return null
  }
}

const Preview = ({ file }: PreviewProps) => {
  const src = file ? toPreviewSrc(file) : null
  if (!src) return null
  return (
    <iframe
      src={src}
      title="Document"
      className="w-full h-96 border-base-300 border-2"
    />
  )
}

type ParsedDataProps<TResult> = {
  result?: TResult
}

const ParsedData = <TResult,>({ result }: ParsedDataProps<TResult>) => {
  if (!result) return null
  return (
    <div>
      <AdminCode data={result} />
    </div>
  )
}
